import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import {Box, Typography} from '@mui/material';

import {surveyStatisticBars} from './SurveyStatisticBars.colors';
import {SurveyStatisticOmittedSection} from './SurveyStatisticOmittedSection';
import {TextOverflowWithTooltip} from '../../../../../../../components/TextOverflowWithTooltip/TextOverflowWithTooltip';
import {LanguageContext} from '../../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, neutral} from '../../../../../../../layouts/theme/designSystem';
import {RespondentsPerDepartment} from '../../../types/SurveyTypes';

interface Props {
  departments?: RespondentsPerDepartment[];
  isOmitted?: boolean;
}

export const DepartmentDistributionBars = ({departments = [], isOmitted = false}: Props) => {
  const {t} = useTranslation();
  const currentLanguage = useContext(LanguageContext) as 'en' | 'de';
  const totalRespondents = departments.reduce((sum, department) => sum + (department.respondents ?? 0), 0);
  const hasDepartments = departments.length > 0 && totalRespondents > 0;

  if (isOmitted) {
    return (
      <SurveyStatisticOmittedSection
        title={t('companySurveys.details.departments')}
        icon={<BusinessOutlinedIcon sx={{fontSize: 14}} />}
        message={t('companySurveys.details.notCollectedInSurvey')}
      />
    );
  }

  if (!hasDepartments) return null;

  const getPercentage = (respondents?: number) => {
    if (!respondents || totalRespondents <= 0) return 0;
    return Math.round((respondents / totalRespondents) * 100);
  };

  return (
    <Box>
      <Box
        sx={{
          alignItems: 'center',
          color: greenColors[700],
          display: 'flex',
          gap: '5px',
          marginBottom: '12px',
        }}
      >
        <BusinessOutlinedIcon sx={{fontSize: 14}} />
        <Typography
          sx={{
            color: 'inherit',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            lineHeight: 1.4,
            textTransform: 'uppercase',
          }}
        >
          {t('companySurveys.details.departments')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: '12px',
          gridTemplateColumns: '1fr',
        }}
      >
        {departments.map((department) => {
          const respondents = department.respondents ?? 0;
          const percentage = getPercentage(respondents);
          const departmentName = department.department.name[currentLanguage] ?? department.department.name.en ?? '';

          return (
            <Box
              key={department.department.resourceId}
              sx={{
                alignItems: 'center',
                display: 'grid',
                gap: '8px',
                gridTemplateColumns: 'minmax(90px, 150px) minmax(0, 1fr) 42px',
              }}
            >
              <TextOverflowWithTooltip
                variant='body1'
                text={departmentName}
                sxTypography={{
                  color: neutral[700],
                  fontSize: '12px',
                  lineHeight: 1.4,
                }}
              />
              <Box
                sx={{
                  backgroundColor: surveyStatisticBars.track,
                  borderRadius: '99px',
                  height: '6px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: surveyStatisticBars.fill,
                    borderRadius: '99px',
                    height: '100%',
                    width: `${percentage}%`,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  color: percentage > 0 ? greenColors[700] : neutral[500],
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: 1.4,
                  textAlign: 'right',
                }}
              >
                {percentage}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
